import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { DetailPageLayout } from '../../../components/layouts';
import { usePageTitle } from '../../../hooks/shared';
import { civicAPI } from '../api/civic';
import ElectionDetailStoryMobile from '../components/ElectionDetailStoryMobile';
import ElectionVotingTimes from '../components/ElectionVotingTimes';

const TYPE_STYLES = {
  general: 'bg-primary-100 text-primary-700',
  primary: 'bg-purple-100 text-purple-700',
  special: 'bg-amber-100 text-amber-700',
  special_primary: 'bg-amber-100 text-amber-700',
  special_runoff: 'bg-orange-100 text-orange-700',
  runoff: 'bg-orange-100 text-orange-700',
  judicial: 'bg-sky-100 text-sky-700',
  nonpartisan: 'bg-teal-100 text-teal-700',
};

function typeLabel(t) {
  return (
    {
      general: 'General',
      primary: 'Primary',
      special: 'Special',
      special_primary: 'Special Primary',
      special_runoff: 'Special Runoff',
      runoff: 'Runoff',
      judicial: 'Judicial',
      nonpartisan: 'Nonpartisan',
    }[t] ?? t
  );
}

function daysUntil(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target - today) / 86400000);
}

function isDateInRange(targetDate, startDate, endDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${targetDate}T00:00:00`);
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  return target >= start && target <= end;
}

function CountdownBadge({ electionDay, earlyVotingStart, earlyVotingEnd }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // If we're in the early voting window, show countdown to both voting day and early voting end
  if (
    earlyVotingStart &&
    earlyVotingEnd &&
    isDateInRange(today, earlyVotingStart, earlyVotingEnd)
  ) {
    const daysUntilEnd = daysUntil(earlyVotingEnd);
    const daysUntilElection = daysUntil(electionDay);
    return (
      <div className="space-y-2">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-orange-600 tabular-nums">{daysUntilEnd}</span>
          <span className="text-sm text-neutral-500">
            day{daysUntilEnd !== 1 ? 's' : ''} until early voting ends
          </span>
        </div>
        <div className="flex items-baseline gap-1 text-sm">
          <span className="font-semibold text-primary-700">{daysUntilElection}</span>
          <span className="text-neutral-500">to Election Day</span>
        </div>
      </div>
    );
  }

  // If early voting hasn't started yet, count down to it
  if (earlyVotingStart) {
    const daysUntilEarly = daysUntil(earlyVotingStart);
    if (daysUntilEarly > 0) {
      const daysUntilElection = daysUntil(electionDay);
      return (
        <div className="space-y-2">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-primary-700 tabular-nums">
              {daysUntilEarly}
            </span>
            <span className="text-sm text-neutral-500">
              day{daysUntilEarly !== 1 ? 's' : ''} until early voting
            </span>
          </div>
          <div className="flex items-baseline gap-1 text-sm">
            <span className="font-semibold text-neutral-600">{daysUntilElection}</span>
            <span className="text-neutral-500">to Election Day</span>
          </div>
        </div>
      );
    }
  }

  // Default: countdown to election day
  const days = daysUntil(electionDay);
  if (days < 0) return <span className="text-sm text-neutral-400">Election has passed</span>;
  if (days === 0)
    return <span className="text-sm font-bold text-red-600">Election Day — today!</span>;
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-5xl font-bold text-primary-700 tabular-nums">{days}</span>
      <span className="text-lg text-neutral-500">day{days !== 1 ? 's' : ''} away</span>
    </div>
  );
}

export default function ElectionDetail() {
  const { id } = useParams();
  const location = useLocation();
  const fromAddress = location.state?.address ?? null;
  const fromState = location.state?.stateCode ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['election', id],
    queryFn: () => civicAPI.getElection(id),
  });

  const { data: newsData, isLoading: newsLoading } = useQuery({
    queryKey: ['election-news', id],
    queryFn: () => civicAPI.getElectionNews(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });

  const election = data?.election;
  const related = data?.relatedElections ?? [];

  usePageTitle(election?.name ?? null);

  const days = election ? daysUntil(election.electionDay) : 0;
  const isPast = days < 0;
  const dateObj = election ? new Date(`${election.electionDay}T00:00:00`) : null;

  // Detect mobile viewport and handle resizes
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Return mobile scrollytelling layout
  if (isMobile) {
    return (
      <ElectionDetailStoryMobile
        election={election}
        newsData={newsData}
        newsLoading={newsLoading}
        related={related}
        fromAddress={fromAddress}
        fromState={fromState}
      />
    );
  }

  // Return desktop traditional layout
  return (
    <DetailPageLayout
      title={election?.name}
      breadcrumbs={[{ label: 'Elections', url: '/elections' }, { label: election?.name }]}
      isLoading={isLoading}
      error={error}
      hero={
        election && (
          <div>
            {fromAddress && (
              <div
                className="mb-4 flex items-center gap-2 px-3 py-2"
                style={{ background: '#ede5d4', border: '1px dashed #8a7e6b' }}
              >
                <span
                  style={{
                    fontFamily: "'Special Elite', monospace",
                    fontSize: '12px',
                    color: '#504636',
                  }}
                >
                  📍 Showing info for: {fromAddress}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-start gap-2 mb-4">
              <span
                className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${TYPE_STYLES[election.electionType] ?? 'bg-neutral-100 text-neutral-600'}`}
              >
                {typeLabel(election.electionType)}
              </span>
              {election.state && (
                <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-600">
                  {election.state}
                </span>
              )}
              {!isPast && (
                <span className="ml-auto px-2.5 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700">
                  Upcoming
                </span>
              )}
            </div>

            {/* Date — most prominent element on the page */}
            <div className="mb-5 pb-5 border-b border-neutral-100">
              <p className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Election Day</p>
              <p className="text-3xl md:text-4xl font-bold text-neutral-900 mb-3 leading-tight">
                {dateObj.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              <CountdownBadge
                electionDay={election.electionDay}
                earlyVotingStart={election.earlyVotingStart}
                earlyVotingEnd={election.earlyVotingEnd}
              />
            </div>

            {/* Election name as context below the date */}
            <h1 className="text-lg md:text-xl font-serif font-semibold text-neutral-600 mb-4 leading-snug">
              {election.name}
            </h1>

            {/* Voting times */}
            <div className="py-3 border-t border-neutral-100">
              <ElectionVotingTimes
                electionDay={election?.electionDay}
                earlyVotingStart={election?.earlyVotingStart}
                scheduleWindows={election?.scheduleWindows}
              />
            </div>
          </div>
        )
      }
      sections={[
        {
          title: 'Quick Links',
          content: (
            <div className="space-y-2">
              <a
                href={`https://duckduckgo.com/?q=${encodeURIComponent(fromAddress ? `!ducky sample ballot ${fromAddress} ${election?.name} site:gov` : `!ducky sample ballot ${election?.name} site:gov`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-neutral-700 hover:text-primary-600 transition-colors py-1"
              >
                <svg
                  className="w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                View Sample Ballot
              </a>
              <a
                href={`https://duckduckgo.com/?q=${encodeURIComponent(fromAddress ? `!ducky polling location ${fromAddress} site:gov` : `!ducky polling location ${election?.name} site:gov`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-neutral-700 hover:text-primary-600 transition-colors py-1"
              >
                <svg
                  className="w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Find Polling Location
              </a>
              <a
                href={`https://duckduckgo.com/?q=${encodeURIComponent(fromState ? `!ducky ${fromState} voter registration site:gov` : `!ducky voter registration ${election?.name} site:gov`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-neutral-700 hover:text-primary-600 transition-colors py-1"
              >
                <svg
                  className="w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                Check Registration
              </a>
            </div>
          ),
        },
        ...(election?.registrationDeadline || election?.absenteeBallotDeadline
          ? [
              {
                title: 'Key Deadlines',
                content: (
                  <div className="space-y-4">
                    {election?.registrationDeadline && (
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
                          Voter Registration Deadline
                        </p>
                        <p className="text-sm font-medium text-neutral-800">
                          {new Date(`${election.registrationDeadline}T00:00:00`).toLocaleDateString(
                            'en-US',
                            {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }
                          )}
                        </p>
                      </div>
                    )}

                    {election?.absenteeBallotDeadline && (
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">
                          Absentee Ballot Deadline
                        </p>
                        <p className="text-sm font-medium text-neutral-800">
                          {new Date(
                            `${election.absenteeBallotDeadline}T00:00:00`
                          ).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                ),
              },
            ]
          : []),
        ...(newsLoading || newsData?.articles?.length > 0
          ? [
              {
                title: 'In the News',
                content: (
                  <div>
                    {newsLoading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="animate-pulse">
                            <div className="h-4 bg-neutral-200 rounded w-3/4 mb-1" />
                            <div className="h-3 bg-neutral-100 rounded w-1/3" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="divide-y divide-neutral-100">
                        {newsData?.articles?.map((article, i) => (
                          <div key={i} className="py-3 first:pt-0 last:pb-0">
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-neutral-800 hover:text-primary-600 transition-colors font-medium leading-snug block mb-1"
                            >
                              {article.title}
                            </a>
                            <p className="text-xs text-neutral-400">
                              {article.source && <span>{article.source}</span>}
                              {article.source && article.publishedAt && <span> · </span>}
                              {article.publishedAt && (
                                <span>
                                  {new Date(article.publishedAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}
                                </span>
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ),
              },
            ]
          : []),
        ...(related.length > 0
          ? [
              {
                title: `Other Elections in ${election?.state}`,
                content: (
                  <div className="divide-y divide-neutral-100">
                    {related.map((e) => (
                      <Link
                        key={e.id}
                        to={`/elections/${e.id}`}
                        className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0 hover:text-primary-600 transition-colors group"
                      >
                        <span className="text-sm text-neutral-800 group-hover:text-primary-600 truncate">
                          {e.name}
                        </span>
                        <span className="shrink-0 text-xs text-neutral-400">
                          {new Date(`${e.electionDay}T00:00:00`).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </Link>
                    ))}
                  </div>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}
